import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import CouponRedemptionListPage from "@saleor/coupons/components/CouponRedemptionListPage";
import { useCouponRedemptionListParentQuery } from "@saleor/coupons/queries";
import {
  couponRedemptionListUrl,
  CouponRedemptionListUrlFilters,
  CouponsRedemptionListUrlQueryParams
} from "@saleor/coupons/url";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import {
  CouponRedemptionSortField,
  OrderDirection
} from "@saleor/types/globalTypes";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";

import {
  getFilterOpts,
  getFilterQueryParam,
  getFilterVariables
} from "./filters";
import { getSortQueryVariables } from "./sort";
interface CouponRedemptionListProps {
  params: CouponsRedemptionListUrlQueryParams;
  id: string;
}

export const CouponRedemptionList: React.FC<CouponRedemptionListProps> = ({
  params,
  id
}) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const shop = useShop();
  const currencySymbol = shop?.defaultCurrency || "PHP";
  const { updateListSettings, settings } = useListSettings(
    ListViews.COUPON_REDEMPTION_LIST
  );

  const changeFilterField = (filter: CouponRedemptionListUrlFilters) => {
    navigate(
      couponRedemptionListUrl(encodeURIComponent(id), {
        ...filter,
        activeTab: params.activeTab
      })
    );
  };

  const filterOpts = getFilterOpts(params);

  const paginationState = createPaginationState(
    settings?.rowNumber ? settings?.rowNumber : 20,
    params
  );
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  const handleSort = createSortHandler(
    navigate,
    params => couponRedemptionListUrl(id, params),
    params
  );

  React.useEffect(
    () =>
      navigate(
        couponRedemptionListUrl(id, {
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings?.rowNumber]
  );

  const handleSearchChange = (value: string) => {
    const filters = {
      ...params,
      query: value
    };
    changeFilterField(filters);
  };

  const checkParamsSort = () => {
    if (params.sort === "claimedAt" || params.sort === "redeemedAt") {
      return getSortQueryVariables(params);
    } else {
      return {
        direction: OrderDirection.DESC,
        field: CouponRedemptionSortField.CLAIMED_AT
      };
    }
  };
  const { data, loading } = useCouponRedemptionListParentQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      id,
      sortBy: checkParamsSort()
    }
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data?.coupon.couponRedemptions.pageInfo),
    paginationState,
    params
  );

  const [changeFilters] = createFilterHandlers({
    createUrl: (params?: CouponsRedemptionListUrlQueryParams) =>
      couponRedemptionListUrl(encodeURIComponent(id), params),
    getFilterQueryParam,
    navigate,
    params
  });

  return (
    <>
      <CouponRedemptionListPage
        couponCode={maybe(() => data?.coupon.code)}
        brands={null}
        couponRedemptions={maybe(() =>
          data.coupon.couponRedemptions.edges.map(edge => edge.node)
        )}
        couponChild={maybe(() =>
          data.coupon.couponRedemptions.edges.map(edge => edge.node)
        )}
        couponStatus={maybe(() => data?.coupon.status)}
        couponAvailability={maybe(() => data?.coupon.availableQuantity)}
        couponCount={maybe(() => data?.coupon.quantity)}
        params={params}
        onAdd={undefined}
        disabled={loading}
        isFeatured={maybe(() => data?.coupon.isFeatured)}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onRowClick={undefined}
        currentTab={0}
        tabs={[]}
        settings={settings}
        pageInfo={pageInfo}
        onTabChange={undefined}
        onTabDelete={undefined}
        onTabSave={undefined}
        filterOpts={filterOpts}
        currencySymbol={currencySymbol}
        onFilterChange={changeFilters}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onUpdateListSettings={updateListSettings}
        sort={getSortParams(params)}
        onSort={handleSort}
      />
    </>
  );
};
export default CouponRedemptionList;
